import { TABLE_COLUMN_MAP, HEADER_ROW_STYLES, HEADER_CELL_STYLES, DATA_ROW_STYLES, DATA_CELL_STYLES } from '../consts';
import useFilters from '../hooks/useFilters';
import OrderDetails from './OrderDetails';
import OrdersFooter from './OrdersFooter';
import Skeleton from './Skeleton';
import { useQuery } from '@tanstack/react-query';
import { getOrders, getOrderStatus, getProducts } from 'api/requests/giftCard/giftCard';
import { GiftCardOrder, GiftCardProduct } from 'api/types/giftCard/giftCard';
import { ConnectionErrorIllustration } from 'assets/illustrations';
import { ForwardIcon } from 'assets/svg';
import { useState, useMemo } from 'react';
import { queryKeyBuilder } from 'shared/query';
import { isNonEmptyArray } from 'shared/utils/bids';

function Orders() {
  const urlQueryParams = useFilters();

  const {
    data: productsData,
    isPending: areProductsPending,
    isError: areProductsErrored,
  } = useQuery({
    queryKey: queryKeyBuilder.giftCardsProducts(),
    queryFn: getProducts,
    select: (resp) => resp.data,
  });

  const {
    data: statusesData,
    isPending: areStatusesPending,
    isError: areStatusesErrored,
  } = useQuery({
    queryKey: queryKeyBuilder.giftCardsStatuses(),
    queryFn: getOrderStatus,
  });

  const {
    data: ordersData,
    isPending: areOrdersPending,
    isError: areOrdersErrored,
  } = useQuery({
    queryKey: queryKeyBuilder.giftCardsToShow(urlQueryParams),
    queryFn: () => getOrders(urlQueryParams),
    enabled: !!productsData && !!statusesData,
  });

  const productsMap = useMemo(() => {
    const products = (isNonEmptyArray(productsData) ? productsData : []) as Array<GiftCardProduct>;
    return products.reduce((acc, curr) => {
      acc[curr.productId] = curr.name;
      return acc;
    }, {} as Record<number, string>);
  }, [productsData]);

  const areOrdersEmpty = !Array.isArray(ordersData?.orders) || ordersData?.orders.length === 0;
  const [displayedOrderId, setDisplayedOrderId] = useState<string | undefined>(undefined);

  const isError = areOrdersErrored || areProductsErrored || areStatusesErrored;
  const isPending = areOrdersPending || areProductsPending || areStatusesPending;

  return isError ? (
    <div>Error</div>
  ) : isPending ? (
    <Skeleton />
  ) : areOrdersEmpty ? (
    <div className="flex flex-col w-full bg-white rounded-lg divide-y divide-palette-color-bg-grey">
      <div className="w-full grow flex flex-col items-center justify-center p-12">
        <ConnectionErrorIllustration />
        <div className="mt-5 max-w-[360px] text-center font-klarheit text-2xl font-semibold">
          There are no results matching your filters
        </div>
      </div>
    </div>
  ) : (
    <div className="flex w-full overflow-scroll scrollbar">
      <div className="flex flex-col bg-white rounded-lg divide-y divide-palette-color-bg-grey w-max lg:w-full">
        <div className={HEADER_ROW_STYLES}>
          {TABLE_COLUMN_MAP.map((cellSchema, index) => (
            <div key={index} className={`${HEADER_CELL_STYLES} flex-1`}>
              {cellSchema.headerLabel}
            </div>
          ))}
          <div className={`${HEADER_CELL_STYLES} w-4`} />
        </div>
        {ordersData?.orders.map((order) => (
          <div key={order.id} className={DATA_ROW_STYLES} onClick={() => setDisplayedOrderId(order?.id)}>
            {TABLE_COLUMN_MAP.map((cellSchema) => (
              <div key={`${order.id}_${cellSchema.key}`} className={`${DATA_CELL_STYLES} flex-1`}>
                {cellSchema.key === 'created_at'
                  ? new Date(order?.[cellSchema.key as keyof GiftCardOrder] ?? '').toISOString().slice(0, 10)
                  : cellSchema.key === 'productType'
                  ? productsMap[order?.productId] ?? ''
                  : order?.[cellSchema.key as keyof GiftCardOrder] ?? ''}
              </div>
            ))}
            <div className={`${DATA_CELL_STYLES} w-4`}>
              <ForwardIcon className="w-3 h-3 text-black transition-transform cursor-pointer" />
            </div>
          </div>
        ))}
        <OrdersFooter />
        <OrderDetails orderId={displayedOrderId} onModalClose={() => setDisplayedOrderId(undefined)} />
      </div>
    </div>
  );
}

export default Orders;
